import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import useToast, { ToastType } from '../../../hooks/useToast'
import useToggle from '../../../hooks/useToggle'
import useTranslation from '../../../hooks/useTranslation'
import { HERO_FEED_SLUG, HERO_SLOT_COUNT } from '../constants'
import {
  contentFeedsQuery,
  IContentFeed,
  IContentFeedBoost,
  IContentFeedsResponse
} from '../queries/getContentFeeds'
import useFeedBoosts from './useFeedBoosts'
import useFeedSettings from './useFeedSettings'
import { INewFeedInput } from '../components/NewContentFeedModal'
import {
  IUpsertContentFeedResponse,
  IUpsertContentFeedVariables,
  upsertContentFeedMutation
} from '../mutations/upsertContentFeed'
import {
  ISetContentFeedTopicsResponse,
  ISetContentFeedTopicsVariables,
  setContentFeedTopicsMutation
} from '../mutations/setContentFeedTopics'

const SUCCESS_TOAST_MS = 3000
const ERROR_TOAST_MS = 8000

/**
 * Mirrors the server's `ORDER BY displayOrder ASC, name ASC` with Postgres'
 * NULLs-last behaviour. Sorting client-side rather than trusting the array
 * order matters after a reorder: the mutations update each feed's displayOrder
 * in the Apollo cache, but the cached root query keeps its original array
 * order, so re-deriving the list has to re-sort.
 */
export const sortFeeds = (feeds: IContentFeed[]): IContentFeed[] =>
  [...feeds].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      if (a.displayOrder === null) return 1
      if (b.displayOrder === null) return -1
      return a.displayOrder - b.displayOrder
    }
    return a.name.localeCompare(b.name)
  })

const useContentFeeds = () => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const {
    isOn: isCreateOpen,
    turnOn: openCreate,
    turnOff: closeCreate
  } = useToggle()

  const { data, loading, error, refetch } = useQuery<IContentFeedsResponse>(
    contentFeedsQuery,
    { variables: { includeDisabled: true } }
  )

  const feeds = data?.contentFeeds ?? []
  const heroFeed = feeds.find(feed => feed.slug === HERO_FEED_SLUG) ?? null
  const topicFeeds = feeds.filter(feed => feed.slug !== HERO_FEED_SLUG)

  const {
    boosts: heroBoosts,
    loading: boostsLoading,
    error: boostsError,
    changeCount: heroBoostChanges,
    reorder: reorderHeroBoosts,
    remove: removeHeroBoost,
    replaceAt: replaceHeroBoost,
    discard: discardHeroBoosts,
    publish: publishHeroBoosts
  } = useFeedBoosts(heroFeed?.id)

  // Which slot the picker is filling. null means closed.
  const [heroSlotIndex, setHeroSlotIndex] = useState<number | null>(null)
  const chooseHeroSlot = (index: number) => setHeroSlotIndex(index)
  const closeHeroSelect = () => setHeroSlotIndex(null)

  const confirmHeroSelection = (picked: IContentFeedBoost[]) => {
    if (heroSlotIndex === null || !picked.length) return
    replaceHeroBoost(heroSlotIndex, picked[0])
    closeHeroSelect()
  }

  const [upsertContentFeed] = useMutation<
    IUpsertContentFeedResponse,
    IUpsertContentFeedVariables
  >(upsertContentFeedMutation)
  const [setContentFeedTopics] = useMutation<
    ISetContentFeedTopicsResponse,
    ISetContentFeedTopicsVariables
  >(setContentFeedTopicsMutation)

  // --- hero settings, staged alongside its boosts --------------------------

  const {
    settings: heroSettings,
    changeCount: heroSettingsChanges,
    update: updateHeroSettings,
    reset: resetHeroSettings,
    publish: publishHeroSettings
  } = useFeedSettings(heroFeed)

  const [heroPublishing, setHeroPublishing] = useState(false)
  const {
    isOn: isHeroSettingsOpen,
    turnOn: openHeroSettings,
    turnOff: hideHeroSettings
  } = useToggle()

  // Publish commits the featured content only; the settings save on Apply.
  const heroChangeCount = heroBoostChanges

  /**
   * The band must be completely full, not merely non-empty -- see the note on
   * HERO_SLOT_COUNT. Publishing and visibility are the two ways to put members
   * in front of the band, so both check this and neither is allowed to pass.
   *
   * Held off until the boosts have loaded: an empty list mid-fetch is not an
   * incomplete band, and flashing the warning while it resolves would train
   * curators to ignore it.
   */
  const heroIsIncomplete = !boostsLoading && heroBoosts.length < HERO_SLOT_COUNT

  const heroPublishBlockedReason = heroIsIncomplete
    ? t('contentCuration:hero:publishBlocked', { required: HERO_SLOT_COUNT })
    : null

  /**
   * The header toggle edits a settings field, so it saves immediately like Apply
   * rather than waiting for Publish -- otherwise the same field would behave two
   * different ways depending on which control you used.
   */
  const toggleHeroEnabled = async () => {
    const enabled = !heroSettings.enabled

    // Turning it OFF is always allowed: hiding an incomplete band is the fix,
    // not the problem. Only the way in is guarded.
    if (enabled && heroIsIncomplete) {
      showToast(
        t('contentCuration:hero:visibilityBlocked', {
          required: HERO_SLOT_COUNT
        }),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
      return
    }

    updateHeroSettings({ enabled })
    try {
      await upsertContentFeed({
        variables: {
          id: heroFeed?.id,
          name: heroSettings.name.trim() || heroFeed?.name || '',
          enabled
        }
      })
    } catch (mutationError: any) {
      resetHeroSettings()
      showToast(
        mutationError?.message ?? t('contentCuration:settings:applyFailed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
    }
  }

  /** Commits the settings on its own -- see applySettings on the detail page. */
  const applyHeroSettings = async () => {
    if (!heroFeed || !heroSettingsChanges || heroPublishing) return
    setHeroPublishing(true)
    try {
      await publishHeroSettings()
      hideHeroSettings()
      showToast(
        t('contentCuration:settings:applied'),
        ToastType.INFO,
        SUCCESS_TOAST_MS
      )
    } catch (mutationError: any) {
      showToast(
        mutationError?.message ?? t('contentCuration:settings:applyFailed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
    } finally {
      setHeroPublishing(false)
    }
  }

  const closeHeroSettings = () => {
    if (heroSettingsChanges && !window.confirm(t('common:unsavedChanges'))) {
      return
    }
    resetHeroSettings()
    hideHeroSettings()
  }

  // Discard is the content bar's action, so it reverts the content only --
  // settings are already saved by the time this can be pressed.
  const discardHero = () => discardHeroBoosts()

  const publishHero = async () => {
    if (!heroFeed || !heroBoostChanges || heroPublishing) return
    // The bar's button is already disabled; this keeps the rule true even if a
    // future caller reaches publish another way.
    if (heroIsIncomplete) return
    setHeroPublishing(true)
    try {
      await publishHeroBoosts()
      showToast(
        t('contentCuration:publish:published'),
        ToastType.INFO,
        SUCCESS_TOAST_MS
      )
    } catch (mutationError: any) {
      // The draft is kept so the curator can retry rather than redo.
      showToast(
        mutationError?.message ?? t('contentCuration:publish:failed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
    } finally {
      setHeroPublishing(false)
    }
  }

  // Drag needs its own copy so the list moves under the pointer before the
  // round trip. Resync is suppressed mid-reorder so a partially applied batch
  // cannot make rows jump while the rest of the writes are still in flight.
  const [orderedFeeds, setOrderedFeeds] = useState<IContentFeed[]>([])
  const [orderPublishing, setOrderPublishing] = useState(false)
  const isReorderingRef = useRef(false)

  useEffect(() => {
    const server = sortFeeds(topicFeeds)

    if (!isReorderingRef.current) {
      setOrderedFeeds(server)
      return
    }

    // A reorder draft is pending, so the server's ORDER must not overwrite it.
    // Its MEMBERSHIP still has to be adopted, though: bailing outright meant a
    // feed created during an unpublished reorder never appeared in the list,
    // because createFeed's refetch was thrown away. Keep the drafted order for
    // feeds that still exist, take their latest data, drop deleted ones, and
    // append anything new -- a new feed has a null displayOrder, so the end is
    // where the server would have put it anyway.
    setOrderedFeeds(current => {
      const byId = new Map(server.map(feed => [feed.id, feed]))
      const kept = current
        .map(feed => byId.get(feed.id))
        .filter((feed): feed is IContentFeed => !!feed)
      const keptIds = new Set(kept.map(feed => feed.id))
      return [...kept, ...server.filter(feed => !keptIds.has(feed.id))]
    })
  }, [data])

  const createFeed = async (input: INewFeedInput) => {
    try {
      const result = await upsertContentFeed({
        variables: {
          name: input.name.trim(),
          description: input.description.trim() || null,
          enabled: input.enabled
        }
      })

      // Topics need the new id, so they are a second write. A failure here
      // leaves the feed created but untopiced -- recoverable from its settings,
      // and better than refusing to create the feed at all.
      const created = result.data?.upsertContentFeed
      if (created && input.topicIds.length) {
        await setContentFeedTopics({
          variables: { contentFeedId: created.id, topicIds: input.topicIds }
        })
      }

      // The server assigns the id and sorts the list, so take its answer.
      await refetch()
      closeCreate()
      showToast(
        t('contentCuration:toast:created', { name: input.name.trim() }),
        ToastType.INFO,
        SUCCESS_TOAST_MS
      )
    } catch (mutationError: any) {
      // The server's uniqueness message already reads well; pass it through.
      showToast(
        mutationError?.message ?? t('contentCuration:toast:createFailed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
    }
  }

  // A drag is a draft, like everything else on this page. isReorderingRef also
  // keeps the resync effect above from pulling the server order back over it.
  const reorder = (next: IContentFeed[]) => {
    isReorderingRef.current = true
    setOrderedFeeds(next)
  }

  /**
   * Which feeds would need writing, normalising the visible list to 1..N.
   *
   * Normalising the whole list rather than just the moved feed is self-healing:
   * displayOrder is nullable and currently sparse, so writing one row would
   * leave the order ambiguous. The hero feed is left out -- it is pulled from
   * the list by slug, so its position among the topic rows is meaningless.
   */
  const pendingOrder = orderedFeeds
    .map((feed, index) => ({ feed, displayOrder: index + 1 }))
    .filter(({ feed, displayOrder }) => feed.displayOrder !== displayOrder)

  // One, not one per row: "moved 3 feeds" is not something a curator acts on
  // differently from "moved one". Matches how the boost lists count.
  const feedOrderChanges = pendingOrder.length ? 1 : 0

  const discardFeedOrder = () => {
    isReorderingRef.current = false
    setOrderedFeeds(sortFeeds(topicFeeds))
  }

  const publishFeedOrder = async () => {
    if (!pendingOrder.length || orderPublishing) return
    setOrderPublishing(true)
    try {
      await Promise.all(
        pendingOrder.map(({ feed, displayOrder }) =>
          upsertContentFeed({
            // name is required on every upsert server-side, so resend it.
            variables: { id: feed.id, name: feed.name, displayOrder }
          })
        )
      )
      isReorderingRef.current = false
      showToast(
        t('contentCuration:publish:published'),
        ToastType.INFO,
        SUCCESS_TOAST_MS
      )
    } catch (mutationError: any) {
      // These are independent writes, so some may have landed. Refetch rather
      // than guess, and let the resync show what actually persisted.
      isReorderingRef.current = false
      showToast(
        mutationError?.message ?? t('contentCuration:publish:failed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
      await refetch()
    } finally {
      setOrderPublishing(false)
    }
  }

  return {
    loading,
    error,
    heroFeed,
    heroBoosts,
    boostsLoading,
    boostsError,
    heroFeedSettings: heroSettings,
    updateHeroSettings,
    isHeroSettingsOpen,
    openHeroSettings,
    closeHeroSettings,
    applyHeroSettings,
    heroSettingsChanges,
    toggleHeroEnabled,
    heroChangeCount,
    heroPublishBlockedReason,
    heroPublishing,
    publishHero,
    discardHero,
    reorderHeroBoosts,
    removeHeroBoost,
    heroSlotIndex,
    chooseHeroSlot,
    closeHeroSelect,
    confirmHeroSelection,
    orderedFeeds,
    isCreateOpen,
    openCreate,
    closeCreate,
    createFeed,
    reorder,
    feedOrderChanges,
    orderPublishing,
    publishFeedOrder,
    discardFeedOrder
  }
}

export default useContentFeeds
